import { useEffect, useRef, useState } from "react";
import { type CelebrationEvent, useSoundEnabled } from "./PublicCelebrationController";

const DURATIONS: Record<string, number> = {
  six: 3000,
  four: 2500,
  wicket: 2500,
  caught: 2500,
  golden_duck: 3200,
};

const prefersReducedMotion =
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

interface Props {
  event: CelebrationEvent;
  onDone: () => void;
}

export default function PublicCelebrationOverlay({ event, onDone }: Props) {
  const [soundEnabled] = useSoundEnabled();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const dur = DURATIONS[event.type] ?? 2500;
    timerRef.current = setTimeout(onDone, dur);
    return () => clearTimeout(timerRef.current);
  }, [event.id, onDone]);

  useEffect(() => {
    if (!soundEnabled) return;
    playSounds(event.type);
  }, [event.id, soundEnabled]);

  return (
    <div
      className="fixed inset-0 z-[9999] pointer-events-none"
      role="status"
      aria-live="polite"
      aria-label={`${event.type.replace("_", " ")} celebration`}
    >
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-full h-full overflow-hidden">
        {event.type === "six" && <SixEffect />}
        {event.type === "four" && <FourEffect />}
        {event.type === "wicket" && <WicketEffect />}
        {event.type === "caught" && <CaughtEffect />}
        {event.type === "golden_duck" && <GoldenDuckEffect />}
      </div>
    </div>
  );
}

/* ─── Sound Helper ─── */

function playSounds(type: string) {
  const play = (src: string, delay = 0, volume = 0.6) => {
    setTimeout(() => {
      try {
        const a = new Audio(src);
        a.volume = Math.min(volume, 1);
        a.play().catch(() => {});
      } catch { /* ignore */ }
    }, delay);
  };

  switch (type) {
    case "six":
      play("/sfx/six_bat.mp3", 0, 0.7);
      play("/sfx/six_air.mp3", 200, 0.4);
      play("/sfx/six_crowd.mp3", 600, 0.65);
      break;
    case "four":
      play("/sfx/four_bat.mp3", 0, 0.65);
      play("/sfx/four_roll.mp3", 300, 0.4);
      play("/sfx/six_crowd.mp3", 800, 0.5);
      break;
    case "wicket":
      play("/sfx/wkt_stumps.mp3", 0, 0.7);
      play("/sfx/wkt_bails.mp3", 150, 0.5);
      play("/sfx/wkt_howzat.mp3", 400, 0.65);
      break;
    case "caught":
      play("/sounds/bat_hit.mp3", 0, 0.5);
      play("/sounds/crowd_cheer.mp3", 500, 0.6);
      break;
    case "golden_duck":
      play("/sfx/wkt_stumps.mp3", 0, 0.6);
      play("/sfx/wkt_bails.mp3", 150, 0.4);
      break;
  }
}

/* ─── SIX EFFECT ─── */

function SixEffect() {
  return (
    <>
      <FlyingBall />
      <CelebrationText text="SIX!" color="#00C980" delay={300} />
      <Balloons count={7} digit="6" baseDelay={800} />
    </>
  );
}

function FlyingBall() {
  const reduced = prefersReducedMotion;
  return (
    <div
      className="absolute"
      style={{
        left: "5%",
        bottom: "10%",
        animation: reduced
          ? "celebFadeIn 0.3s ease-out forwards"
          : "flyBallSix 1.4s cubic-bezier(0.2,0.8,0.3,1) forwards",
      }}
    >
      <CricketBall size={40} spinning={!reduced} />
      {!reduced && (
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(0,201,128,0.4), transparent 70%)",
            filter: "blur(8px)",
          }}
        />
      )}
    </div>
  );
}

/* ─── FOUR EFFECT ─── */

function FourEffect() {
  return (
    <>
      <Outfield />
      <RollingBall />
      <CelebrationText text="FOUR!" color="#38BDF8" delay={600} />
      <Balloons count={5} digit="4" baseDelay={1000} />
    </>
  );
}

function Outfield() {
  return (
    <div className="absolute bottom-0 left-0 right-0 h-[18%]" style={{ overflow: "hidden" }}>
      <div
        className="absolute inset-0"
        style={{
          background: "linear-gradient(to top, #1a4d2e, #0d3320 60%, transparent)",
          opacity: 0.85,
        }}
      />
      <div
        className="absolute bottom-[35%] right-[10%] h-[4px] rounded-full"
        style={{
          width: "80%",
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.5) 20%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0.5) 80%, transparent)",
          boxShadow: "0 0 6px rgba(255,255,255,0.3)",
        }}
      />
    </div>
  );
}

function RollingBall() {
  const reduced = prefersReducedMotion;
  return (
    <div
      className="absolute"
      style={{
        left: "-5%",
        bottom: "10%",
        animation: reduced
          ? "celebFadeIn 0.3s ease-out forwards"
          : "rollBallFour 1.6s cubic-bezier(0.1,0.4,0.2,1) forwards",
      }}
    >
      <CricketBall size={32} spinning={!reduced} spinSpeed="0.4s" />
    </div>
  );
}

/* ─── WICKET EFFECT ─── */

function WicketEffect() {
  return (
    <>
      <StumpsScene />
      <CelebrationText text="HOWZAT!" color="#EF4444" delay={400} />
    </>
  );
}

function StumpsScene() {
  const reduced = prefersReducedMotion;
  return (
    <div className="absolute bottom-[15%] left-1/2 -translate-x-1/2">
      {/* Three stumps */}
      <div className="relative w-[80px] h-[120px]">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="absolute bottom-0"
            style={{
              left: `${16 + i * 20}px`,
              width: "6px",
              height: "100px",
              background: "linear-gradient(to top, #D4A373, #E8C99B)",
              borderRadius: "2px",
              transformOrigin: "bottom center",
              animation: reduced
                ? "none"
                : `stumpShake 0.3s ${i * 0.05}s ease-out`,
            }}
          />
        ))}
        {/* Bails flying */}
        {[0, 1].map((i) => (
          <div
            key={`bail-${i}`}
            className="absolute"
            style={{
              left: `${18 + i * 22}px`,
              bottom: "98px",
              width: "22px",
              height: "4px",
              background: "#E8C99B",
              borderRadius: "2px",
              animation: reduced
                ? "celebFadeIn 0.3s ease-out forwards"
                : `bailFly${i} 1s 0.15s cubic-bezier(0.2,0.8,0.3,1) forwards`,
            }}
          />
        ))}
        {/* Impact ball */}
        <div
          className="absolute"
          style={{
            right: "120px",
            bottom: "40px",
            animation: reduced
              ? "celebFadeIn 0.3s ease-out forwards"
              : "ballImpact 0.4s cubic-bezier(0.1,0.3,0.3,1) forwards",
          }}
        >
          <CricketBall size={28} spinning={!reduced} spinSpeed="0.3s" />
        </div>
      </div>
    </div>
  );
}

/* ─── CAUGHT EFFECT ─── */

function CaughtEffect() {
  const reduced = prefersReducedMotion;
  return (
    <>
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          style={{
            animation: reduced
              ? "celebFadeIn 0.3s ease-out forwards"
              : "catchSequence 1.8s ease-out forwards",
          }}
        >
          {/* Fielder silhouette with hands up */}
          <svg width="120" height="160" viewBox="0 0 120 160" fill="none" className="drop-shadow-lg">
            {/* Body */}
            <ellipse cx="60" cy="130" rx="20" ry="28" fill="#1a4d2e" />
            {/* Head */}
            <circle cx="60" cy="80" r="16" fill="#2d6b45" />
            {/* Cap */}
            <ellipse cx="60" cy="72" rx="18" ry="6" fill="#0d3320" />
            {/* Arms up catching */}
            <path d="M40 95 L25 55 L35 50" stroke="#2d6b45" strokeWidth="8" strokeLinecap="round" fill="none"
              style={{ animation: reduced ? "none" : "armsCatch 0.8s 0.3s ease-out forwards" }}
            />
            <path d="M80 95 L95 55 L85 50" stroke="#2d6b45" strokeWidth="8" strokeLinecap="round" fill="none"
              style={{ animation: reduced ? "none" : "armsCatch 0.8s 0.3s ease-out forwards" }}
            />
            {/* Ball in hands */}
            <circle cx="60" cy="48" r="8" fill="#C0392B" stroke="#8B1A1A" strokeWidth="1.5"
              style={{
                opacity: 0,
                animation: reduced
                  ? "celebFadeIn 0.3s 0.5s forwards"
                  : "ballAppear 0.3s 0.9s ease-out forwards",
              }}
            />
            {/* Seam on ball */}
            <path d="M54 48 C57 44, 63 52, 66 48" stroke="#8B1A1A" strokeWidth="1" fill="none"
              style={{ opacity: 0, animation: "celebFadeIn 0.2s 1s forwards" }}
            />
          </svg>
        </div>
      </div>
      <CelebrationText text="CAUGHT!" color="#F59E0B" delay={1000} />
    </>
  );
}

/* ─── GOLDEN DUCK EFFECT ─── */

function GoldenDuckEffect() {
  const reduced = prefersReducedMotion;
  return (
    <>
      <div
        className="absolute bottom-[8%]"
        style={{
          animation: reduced
            ? "celebFadeIn 0.5s ease-out forwards"
            : "duckWalk 3s linear forwards",
          left: reduced ? "50%" : undefined,
          transform: reduced ? "translateX(-50%)" : undefined,
        }}
      >
        <GoldenDuckSVG />
      </div>
      <CelebrationText text="GOLDEN DUCK" color="#D6B85A" delay={400} size="lg" />
    </>
  );
}

function GoldenDuckSVG() {
  return (
    <svg width="100" height="90" viewBox="0 0 100 90" fill="none" className="drop-shadow-xl">
      {/* Body */}
      <ellipse cx="45" cy="58" rx="22" ry="18" fill="url(#goldGrad)" />
      {/* Neck */}
      <path d="M60 48 C62 38, 58 28, 55 22" stroke="url(#goldGrad)" strokeWidth="8" fill="none" strokeLinecap="round" />
      {/* Head */}
      <circle cx="55" cy="20" r="10" fill="url(#goldGrad)" />
      {/* Beak (downturned - sad) */}
      <path d="M64 22 L72 24 L64 26" fill="#CC8800" />
      {/* Eye (downcast) */}
      <circle cx="58" cy="18" r="2" fill="#4a3500" />
      <path d="M56 16 L60 17" stroke="#4a3500" strokeWidth="1" strokeLinecap="round" />
      {/* Cricket pads */}
      <rect x="35" y="72" width="7" height="16" rx="2" fill="#E8DCC8" stroke="#C4B89A" strokeWidth="0.5" />
      <rect x="48" y="72" width="7" height="16" rx="2" fill="#E8DCC8" stroke="#C4B89A" strokeWidth="0.5" />
      {/* Feet */}
      <path d="M35 88 L30 90 L38 90 Z" fill="#CC8800" />
      <path d="M48 88 L43 90 L51 90 Z" fill="#CC8800" />
      {/* Bat dragging behind */}
      <rect x="10" y="62" width="5" height="30" rx="2" fill="#D4A373" transform="rotate(-15 12 62)" />
      <rect x="9" y="56" width="7" height="10" rx="1" fill="#8B6914" transform="rotate(-15 12 56)" />
      {/* Gold gradient */}
      <defs>
        <linearGradient id="goldGrad" x1="30" y1="20" x2="70" y2="80" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#F5D77A" />
          <stop offset="50%" stopColor="#D6B85A" />
          <stop offset="100%" stopColor="#B8942E" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/* ─── SHARED SUB-COMPONENTS ─── */

function CricketBall({ size = 36, spinning = true, spinSpeed = "0.6s" }: { size?: number; spinning?: boolean; spinSpeed?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 36 36"
      style={spinning ? { animation: `spin ${spinSpeed} linear infinite` } : undefined}
    >
      <circle cx="18" cy="18" r="16" fill="#C0392B" stroke="#8B1A1A" strokeWidth="1.5" />
      <path d="M8 18 C12 12, 24 12, 28 18" stroke="#F8E8E0" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M8 18 C12 24, 24 24, 28 18" stroke="#F8E8E0" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </svg>
  );
}

function CelebrationText({ text, color, delay = 0, size = "md" }: { text: string; color: string; delay?: number; size?: "md" | "lg" }) {
  const reduced = prefersReducedMotion;
  const fontSize = size === "lg" ? "clamp(28px, 8vw, 56px)" : "clamp(36px, 10vw, 72px)";
  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      style={{
        opacity: 0,
        animation: reduced
          ? `celebFadeIn 0.3s ${delay}ms forwards`
          : `celebTextPunch 0.6s ${delay}ms cubic-bezier(0.175,0.885,0.32,1.275) forwards`,
      }}
    >
      <span
        className="font-black tracking-wider uppercase select-none"
        style={{
          fontSize,
          color,
          textShadow: `0 0 30px ${color}60, 0 2px 8px rgba(0,0,0,0.5)`,
          letterSpacing: "0.08em",
        }}
      >
        {text}
      </span>
    </div>
  );
}

function Balloons({ count, digit, baseDelay }: { count: number; digit: string; baseDelay: number }) {
  const reduced = prefersReducedMotion;
  if (reduced) return null;

  const balloons = Array.from({ length: count }, (_, i) => ({
    left: 10 + (i * 80) / count + Math.random() * 10,
    size: 32 + Math.random() * 16,
    delay: baseDelay + i * 120 + Math.random() * 200,
    duration: 2 + Math.random() * 0.8,
    sway: (Math.random() - 0.5) * 30,
    color: ["#00C980", "#19E69B", "#0A3727", "#D6B85A", "#F5F7F6"][i % 5],
    textColor: i % 5 === 4 ? "#031B13" : "#FFFFFF",
  }));

  return (
    <>
      {balloons.map((b, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            left: `${b.left}%`,
            bottom: "-10%",
            opacity: 0,
            animation: `balloonRise ${b.duration}s ${b.delay}ms cubic-bezier(0.2,0.6,0.4,1) forwards`,
          }}
        >
          <svg width={b.size} height={b.size * 1.3} viewBox="0 0 40 52">
            <ellipse cx="20" cy="20" rx="16" ry="19" fill={b.color} opacity="0.85" />
            <ellipse cx="20" cy="20" rx="14" ry="17" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
            <line x1="20" y1="39" x2="20" y2="52" stroke={b.color} strokeWidth="1" opacity="0.5" />
            <text x="20" y="24" textAnchor="middle" fill={b.textColor} fontSize="16" fontWeight="900" fontFamily="system-ui">
              {digit}
            </text>
          </svg>
        </div>
      ))}
    </>
  );
}

/* ─── CSS KEYFRAMES (injected once) ─── */

const KEYFRAMES = `
@keyframes spin { to { transform: rotate(360deg); } }

@keyframes celebFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes celebTextPunch {
  0% { opacity: 0; transform: scale(0.3); }
  50% { opacity: 1; transform: scale(1.15); }
  70% { transform: scale(0.95); }
  100% { opacity: 1; transform: scale(1); }
}

@keyframes flyBallSix {
  0% { transform: translate(0, 0) scale(1); opacity: 1; }
  30% { transform: translate(25vw, -30vh) scale(1.1); opacity: 1; }
  70% { transform: translate(55vw, -60vh) scale(0.9); opacity: 1; }
  100% { transform: translate(85vw, -85vh) scale(0.7); opacity: 0; }
}

@keyframes rollBallFour {
  0% { transform: translateX(0) translateY(0); opacity: 1; }
  60% { transform: translateX(75vw) translateY(-2vh); opacity: 1; }
  85% { transform: translateX(90vw) translateY(-1vh); opacity: 1; }
  100% { transform: translateX(105vw) translateY(0); opacity: 0; }
}

@keyframes balloonRise {
  0% { opacity: 0; transform: translateY(0) translateX(0); }
  15% { opacity: 0.9; }
  100% { opacity: 0; transform: translateY(-110vh) translateX(var(--sway, 15px)); }
}

@keyframes stumpShake {
  0% { transform: rotate(0); }
  30% { transform: rotate(3deg); }
  60% { transform: rotate(-2deg); }
  100% { transform: rotate(0); }
}

@keyframes bailFly0 {
  0% { transform: translate(0, 0) rotate(0); opacity: 1; }
  100% { transform: translate(-30px, -80px) rotate(-180deg); opacity: 0; }
}

@keyframes bailFly1 {
  0% { transform: translate(0, 0) rotate(0); opacity: 1; }
  100% { transform: translate(25px, -90px) rotate(200deg); opacity: 0; }
}

@keyframes ballImpact {
  0% { transform: translateX(80px); opacity: 1; }
  100% { transform: translateX(0); opacity: 1; }
}

@keyframes catchSequence {
  0% { transform: scale(0.8) translateY(20px); opacity: 0; }
  30% { transform: scale(1) translateY(0); opacity: 1; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
}

@keyframes armsCatch {
  0% { transform: translateY(15px); }
  100% { transform: translateY(0); }
}

@keyframes ballAppear {
  0% { opacity: 0; transform: translateY(-20px); }
  100% { opacity: 1; transform: translateY(0); }
}

@keyframes duckWalk {
  0% { transform: translateX(105vw); }
  15% { transform: translateX(70vw); }
  85% { transform: translateX(15vw); }
  100% { transform: translateX(-20vw); }
}
`;

let keyframesInjected = false;
if (typeof document !== "undefined" && !keyframesInjected) {
  keyframesInjected = true;
  const style = document.createElement("style");
  style.textContent = KEYFRAMES;
  document.head.appendChild(style);
}
